interface JWTPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token and returns its payload
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 */
const decodeJWTPayload = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    const payload = JSON.parse(atob(parts[1])) as JWTPayload;

    if (typeof payload.exp !== 'number') {
      console.error('JWT payload missing valid exp claim');
      return null;
    }

    return payload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Checks if a token expiration timestamp is expired
 * @param exp - The expiration timestamp (in seconds since epoch)
 * @param bufferSeconds - Optional buffer time in seconds (default: 300 = 5 minutes)
 * @returns true if token is expired or within the buffer period
 */
export const isTokenExpired = (exp: number, bufferSeconds = 300): boolean => {
  const now = Math.floor(Date.now() / 1000);
  return exp - now <= bufferSeconds;
};

/**
 * Validates a JWT token's expiration
 * @param token - The JWT token string
 * @param bufferSeconds - Optional buffer time in seconds (default: 300 = 5 minutes)
 * @returns true if token is valid and not expired
 */
export const validateTokenExp = (
  token: string,
  bufferSeconds = 300
): boolean => {
  const payload = decodeJWTPayload(token);

  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  return !isTokenExpired(payload.exp, bufferSeconds);
};
